import flatpickr from "flatpickr";
import { Portuguese } from "flatpickr/dist/l10n/pt.js"

class ShippingRates extends HTMLElement {
    constructor() {
        super();

        this.elements = {
            defaultMessage: this.querySelector('[data-default-message]'),
            button: this.querySelector('[data-toggle-form]'),
            form: this.querySelector('[data-form]'),
            message: this.querySelector('[data-message]'),
            ratesList: this.querySelector('[data-shipping-rates]'),
            zipInput: this.querySelector('[name="zip"]'),
            buttonCheckout: document.querySelectorAll("[data-submit]")
        };

        this.init();
    }

    init() {
        if (!this.elements.button || !this.elements.form || !this.elements.zipInput) {
            console.error('ShippingRates: Elementos essenciais não encontrados');
            return;
        }

        this.addEventListeners();
        if (this.elements.ratesList) this.hideElement(this.elements.ratesList);
        if (this.elements.form) this.hideElement(this.elements.form);
        this.applyCepMask();
        this.disableCheckoutButtons();
    }

    addEventListeners() {
        this.elements.button?.addEventListener('click', () => this.toggleForm());
        this.elements.form?.addEventListener('submit', (event) => this.handleSubmit(event));
        this.elements.zipInput?.addEventListener('input', () => this.formatCep());
    }

    applyCepMask() {
        this.elements.zipInput?.setAttribute('maxlength', '9');
    }

    formatCep() {
        if (!this.elements.zipInput) return;
        let cep = this.elements.zipInput.value.replace(/\D/g, '');
        if (cep.length > 5) {
            cep = cep.replace(/^(\d{5})(\d{1,3})/, '$1-$2');
        }
        this.elements.zipInput.value = cep;
    }

    toggleForm() {
        if (this.elements.form) this.showElement(this.elements.form);
        if (this.elements.ratesList) this.hideElement(this.elements.ratesList);
        this.clearMessage();

        if (this.elements.button?.hasAttribute('shipping-rates')) {
            this.elements.button.textContent = 'Consultar';
            this.elements.button.removeAttribute('shipping-rates');
        }
    }

    getCutoffHour() {
        const agendamento = document.querySelector('#agendamento');
        return Number(agendamento?.dataset?.cutoffHour ?? 17);
    }

    clearMessage() {
        const cutoffHour = this.getCutoffHour();
        if (this.elements.defaultMessage) {
            this.elements.defaultMessage.textContent = `Compre de segunda a sábado até às ${cutoffHour}h e receba no mesmo dia. Exclusivo para entregas para Grande São Paulo.`;
        }
        if (this.elements.message) {
            this.elements.message.textContent = '';
            this.hideElement(this.elements.message);
        }
        this.disableCheckoutButtons();
    }

    showElement(element) {
        element?.setAttribute('show', 'true');
    }

    hideElement(element) {
        element?.removeAttribute('show');
    }

    disableCheckoutButtons() {
        this.elements.buttonCheckout.forEach(button => button.setAttribute('disabled', 'true'));

        document.querySelectorAll("[data-submit]").forEach(button => {
            button.setAttribute("disabled", "true");
            button.setAttribute("data-disabled", "true");
        });
    }

    enableCheckoutButtons() {
        this.elements.buttonCheckout.forEach(button => button.removeAttribute('disabled'));

        document.querySelectorAll("[data-submit]").forEach(button => {
            button.removeAttribute("disabled");
            button.removeAttribute("data-disabled");
        });
    }

    async handleSubmit(event) {
        event.preventDefault();
        this.clearMessage();
        this.hideElement(this.elements.ratesList);

        const buttonSubmit = event.currentTarget.querySelector('button[type="submit"]');
        if (buttonSubmit) {
            buttonSubmit.setAttribute('disabled', 'true');
            buttonSubmit.textContent = 'Consultando...';
        }

        const zip = this.elements.zipInput.value.replace(/\D/g, '');

        try {
            const { state, cep: zipCode, city } = await this.fetchCepData(zip);
            if (!state || !zipCode || !city) {
                throw new Error('Dados do CEP incompletos. Por favor, verifique o CEP e tente novamente.');
            }

            const shippingRates = await this.fetchShippingRates(state, zipCode, city);
            this.displayShippingRates(shippingRates);
            this.updateButton(zip);
        } catch (error) {
            // Erros técnicos (instabilidade da API de frete) não devem esconder o calendário de agendamento.
            // Só bloqueamos o agendamento quando o CEP de fato não tem entrega disponível.
            if (error.isTechnicalError && !error.isUnavailableCep) {
                console.error('Erro técnico ao buscar taxas de envio (permitindo continuar):', error);
                this.handleTechnicalError(zip, buttonSubmit);
                return;
            }

            const message = error.userMessage || (error instanceof Error ? error.message : String(error));
            this.displayError(message);
        }

        if (buttonSubmit) {
            buttonSubmit.removeAttribute('disabled');
            buttonSubmit.textContent = 'Consultar';
        }
    }

    async fetchWithRetry(url, { timeout = 2000, retries = 3 } = {}) {
        const attempt = async () => {
            const controller = new AbortController();
            const timer = setTimeout(() => controller.abort(), timeout);
            try {
                const response = await fetch(url, { signal: controller.signal });
                clearTimeout(timer);
                if (!response.ok) throw new Error('Network response was not ok');
                const text = await response.text();
                if (!text || text.trim().length === 0) throw new Error('Resposta vazia da API de CEP');
                try {
                    return JSON.parse(text);
                } catch (error) {
                    throw new Error('Resposta inválida da API de CEP');
                }
            } catch (error) {
                clearTimeout(timer);
                throw error;
            }
        };

        let lastError;
        for (let i = 0; i < retries; i++) {
            if (i > 0) await this.sleep(500);
            try {
                return await attempt();
            } catch (error) {
                lastError = error;
            }
        }
        throw lastError;
    }

    async fetchCepData(zip) {
        try {
            const data = await this.fetchWithRetry(`https://brasilapi.com.br/api/cep/v1/${zip}`);
            if (data && (data.state || data.uf)) {
                const city = data.city || data.city_name || '';
                if (city) {
                    return { state: data.state || data.uf, cep: data.cep || zip, city };
                }
            }
        } catch (error) {
            // tenta o fallback abaixo
        }

        try {
            const data = await this.fetchWithRetry(`https://viacep.com.br/ws/${zip}/json/`);
            if (data && !data.erro) {
                const city = data.localidade || '';
                if (!city) throw new Error('CEP encontrado mas cidade não disponível. Por favor, verifique o CEP.');
                return { state: data.uf, cep: data.cep || zip, city };
            }
            throw new Error('CEP não encontrado');
        } catch (error) {
            if (error.message && error.message.includes('cidade não disponível')) throw error;
            throw new Error('CEP não encontrado, por favor, verifique se o CEP está correto e tente novamente.');
        }
    }

    async validateCartNotEmpty() {
        let lastError;
        for (let i = 0; i < 3; i++) {
            if (i > 0) await this.sleep(500);
            try {
                const response = await fetch('/cart.js');
                if (!response.ok) throw new Error('Não foi possível verificar o carrinho');
                const text = await response.text();
                if (!text || text.trim().length === 0) throw new Error('Resposta vazia');
                const cart = JSON.parse(text);
                if (!cart.items || cart.items.length === 0) throw new Error('O carrinho está vazio. Adicione itens antes de calcular o frete.');
                return cart;
            } catch (error) {
                lastError = error;
                if (error.message && error.message.includes('vazio')) throw error;
            }
        }
        throw lastError;
    }

    async fetchShippingRates(state, zip, city) {
        try {
            await this.validateCartNotEmpty();
        } catch (error) {
            if (error.message && error.message.includes('vazio')) throw error;
            console.warn('Aviso: Não foi possível validar carrinho, continuando mesmo assim...');
        }

        const normalizedZip = zip.replace(/\D/g, '');
        const formattedZip = normalizedZip.length === 8
            ? `${normalizedZip.substring(0, 5)}-${normalizedZip.substring(5)}`
            : normalizedZip;
        const normalizedCity = city.normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

        const payload = JSON.stringify({
            shipping_address: {
                country: 'BR',
                province: state && state.length === 2 ? state.toUpperCase() : state,
                zip: formattedZip,
                city: normalizedCity
            }
        });

        try {
            let prepared = false;
            try {
                await this.requestShippingRates('/cart/prepare_shipping_rates', payload, null, true);
                prepared = true;
            } catch (error) {
                if (!error.message || !error.message.includes('Resposta vazia')) {
                    if (error.isTechnicalError && error.statusCode === 422) {
                        throw new Error(`Erro ao preparar cálculo de frete: ${error.originalMessage || 'Dados do endereço inválidos. Verifique o CEP e tente novamente.'}`);
                    }
                    throw error;
                }
                prepared = true;
            }

            if (!prepared) throw new Error('Não foi possível preparar o cálculo de frete. Tente novamente.');

            const requestAsyncRates = async () => {
                const response = await fetch('/cart/shipping_rates.json', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'X-Requested-With': 'XMLHttpRequest'
                    },
                    credentials: 'same-origin',
                    body: payload
                });

                if (!response.ok) {
                    const text = await response.text().catch(() => 'Erro desconhecido');
                    throw new Error(`Erro ao consultar frete: ${response.status} - ${text}`);
                }

                const text = await response.text();
                if (!text || text.trim().length === 0) throw new Error('Resposta vazia do servidor');
                try {
                    return JSON.parse(text);
                } catch (error) {
                    throw new Error('Resposta inválida do servidor');
                }
            };

            let shippingRates;
            let lastError;
            const maxAttempts = 3;
            for (let i = 0; i < maxAttempts; i++) {
                if (i > 0) await this.sleep(500);
                try {
                    shippingRates = await requestAsyncRates();
                    break;
                } catch (error) {
                    lastError = error;
                }
            }

            if (shippingRates === undefined) throw lastError;
            return shippingRates;
        } catch (error) {
            console.error('Erro ao buscar taxas de envio:', error);
            if (error instanceof Error && error.message) throw error;
            throw new Error('Não foi possível obter as taxas de envio. Tente novamente.');
        }
    }

    async requestShippingRates(url, body = null, signal = null, allowEmpty = false) {
        let lastError;
        for (let i = 0; i < 3; i++) {
            if (i > 0) await this.sleep(500);
            try {
                const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
                if (body) headers['Content-Type'] = 'application/json';

                const options = { method: body ? 'POST' : 'GET', headers, signal: signal || undefined, credentials: 'same-origin' };
                if (body) options.body = body;

                const response = await fetch(url, options);
                if (signal?.aborted) throw new DOMException('A requisição foi abortada', 'AbortError');

                if (!response.ok) {
                    let text = 'Erro desconhecido';
                    let errorData = null;
                    try {
                        text = await response.text();
                        try { errorData = JSON.parse(text); } catch (error) { /* ignore */ }
                    } catch (error) { /* ignore */ }

                    if (response.status === 422) {
                        const error = new Error('Erro ao calcular frete: dados do endereço inválidos ou incompatíveis');
                        error.isTechnicalError = true;
                        error.statusCode = 422;
                        error.originalMessage = text;
                        error.errorData = errorData;
                        error.userMessage = errorData?.error
                            ? (Array.isArray(errorData.error) ? errorData.error.join('. ') : String(errorData.error))
                            : 'Não foi possível calcular o frete. Verifique o CEP e tente novamente.';
                        throw error;
                    }

                    const error = new Error(`Erro ao consultar frete (${response.status}): ${text.substring(0, 200)}`);
                    error.statusCode = response.status;
                    error.originalMessage = text;
                    error.errorData = errorData;
                    throw error;
                }

                const contentType = response.headers.get('content-type') || '';
                const text = await response.text();
                if (!text || text.trim().length === 0) {
                    if (allowEmpty) return null;
                    if (url.includes('prepare_shipping_rates')) return { status: 'preparing' };
                    throw new Error('Resposta vazia do servidor. Tente novamente.');
                }

                if (!contentType.includes('application/json') && !contentType.includes('text/json') && text.trim().startsWith('<')) {
                    throw new Error('O servidor retornou uma página de erro. Tente novamente.');
                }

                try {
                    return JSON.parse(text);
                } catch (error) {
                    throw new Error('Resposta inválida do servidor. Tente novamente.');
                }
            } catch (error) {
                if (error.name === 'AbortError' || error instanceof DOMException) throw error;
                if (error.isTechnicalError && error.statusCode === 422) throw error;
                lastError = error;
            }
        }

        if (lastError instanceof Error) throw lastError;
        throw new Error('Erro de rede ao consultar frete');
    }

    getUnavailableCepMessage() {
        return this.dataset.shippingUnavailableMessage
            || 'Ops, CEP fora da área de entrega ou itens indisponíveis para sua região (Bolos, Flores). Revise o seu carrinho e tente novamente';
    }

    throwUnavailableCepError() {
        const error = new Error(this.getUnavailableCepMessage());
        error.isUnavailableCep = true;
        throw error;
    }

    displayShippingRates(shippingRates) {
        if (!shippingRates || !shippingRates.shipping_rates) {
            console.error('shippingRates é null ou inválido:', shippingRates);
            this.throwUnavailableCepError();
        }

        // remover taxa de backup se existir
        shippingRates.shipping_rates = shippingRates.shipping_rates
            .filter(rate => !rate.source.toLowerCase().includes('shopify_provided_generic_layer'))
            .filter(rate => !rate.source.toLowerCase().includes('lalamove_carrier_service'));

        if (!shippingRates.shipping_rates.length) {
            this.throwUnavailableCepError();
        }

        // verificar se opção de entrega tem opção de retirada ou Entrega Recomendada - Agendada
        const hasRetirada = shippingRates.shipping_rates.some(rate => {
            const name = rate.name.toLowerCase();
            return name.includes('retirada') || name.includes('retirar') || name.includes('pickup') || name.includes('pick-up');
        });
        const hasEntregaRecomendada = shippingRates.shipping_rates.some(rate => {
            const name = rate.name.toLowerCase();
            return name.includes('entrega recomendada - agendada') || name.includes('entrega agendada') || name.includes('agendada') || name.includes('agendamento');
        });
        const localDelivery = shippingRates.shipping_rates.some(rate => {
            const name = rate.name.toLowerCase();
            return name.includes('local delivery') || name.includes('entrega local');
        });

        if (!hasRetirada && !hasEntregaRecomendada && !localDelivery) {
            console.log("Não há opção de retirada, entrega recomendada ou local delivery disponível.");

            // adicionar mensagem informando que não há opção de retirada
            if (this.elements.defaultMessage) {
                this.elements.defaultMessage.textContent = "Oba! Entregamos no CEP indicado. Mas se quiser retirar diretamente na loja na Vila Olímpia (SP), utilize o CEP 04549-002 e agende o melhor dia para retirada.";
            }

            // liberar o checkout
            this.enableCheckoutButtons();

            return;
        }

        // Adicione este código para exibir o frete
        const freteValorElement = document.getElementById('frete-valor');
        if (freteValorElement && shippingRates.shipping_rates.length > 0) {
            const rate = shippingRates.shipping_rates[0];

            // Garante que o valor é um número
            let valorFrete = 0;

            try {
                // Tenta converter para número, independente do formato
                if (typeof rate.price === 'string') {
                    valorFrete = Number(rate.price.replace(/[^\d.,]/g, '').replace(',', '.'));
                } else {
                    valorFrete = Number(rate.price);
                }

                // Se o valor for muito alto, provavelmente está em centavos
                if (valorFrete > 100) {
                    valorFrete = valorFrete / 100;
                }

                // Se for grátis
                if (valorFrete === 0) {
                    freteValorElement.textContent = "Frete: Grátis";
                } else {
                    freteValorElement.textContent = `Frete: R$ ${valorFrete.toFixed(2).replace('.', ',')}`;
                }
            } catch (error) {
                console.error("Erro ao processar valor do frete:", error);
                freteValorElement.textContent = "Frete: Consultar";
            }
        }

        this.showElement(this.elements.ratesList);
    }

    updateButton(zip) {
        if (this.elements.button) {
            this.elements.button.textContent = zip;
            this.elements.button.setAttribute('shipping-rates', 'true');
        }
        if (this.elements.form) this.hideElement(this.elements.form);
    }

    // Falhas técnicas (instabilidade da API de frete) não devem bloquear o cliente:
    // mantemos o calendário de agendamento visível e liberamos o checkout mesmo assim.
    handleTechnicalError(zip, buttonSubmit) {
        if (buttonSubmit) {
            buttonSubmit.removeAttribute('disabled');
            buttonSubmit.textContent = 'Consultar';
        }

        this.updateButton(zip);
        if (this.elements.ratesList) this.showElement(this.elements.ratesList);
        if (this.elements.message) {
            this.elements.message.textContent = '';
            this.hideElement(this.elements.message);
        }
        if (this.elements.defaultMessage) {
            const cutoffHour = this.getCutoffHour();
            this.elements.defaultMessage.textContent = `Compre de segunda a sábado até às ${cutoffHour}h e receba no mesmo dia. Exclusivo para entregas para Grande São Paulo.`;
        }

        this.enableCheckoutButtons();
        console.warn('Erro técnico ao calcular frete, mas permitindo continuar com o agendamento.');
    }

    displayError(message) {
        if (!this.elements.message) {
            console.error('Elemento de mensagem não encontrado:', message);
            return;
        }

        const text = typeof message === 'string' ? message : 'Ocorreu um erro. Por favor, tente novamente.';
        this.elements.message.textContent = text;
        this.showElement(this.elements.message);
        this.disableCheckoutButtons();
        this.hideElement(this.elements.ratesList);
        if (this.elements.form) this.hideElement(this.elements.form);
    }

    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

customElements.define('shipping-rates', ShippingRates);


class DataSelect extends HTMLElement {
    constructor() {
        super();
        this.today = new Date();
        this.maxDate = new Date();
        this.diasUteisAdicionados = 0;
        this.daysAvailable = 20;
        this.feriadosNacionais = [];
        this.feriadosVariaveis = [];
        this.feriados = [];
        this.init();
    }

    async init() {
        this.daysAvailable = this.getAttribute("data-days-available") ? parseInt(this.getAttribute("data-days-available")) : 20;

        this.getFeriados();
        this.calcularPeriodoDisponivel();
        this.configurarFlatpickr();
    }

    getFeriados() {
        const feriados = this.getAttribute("data-feriados")

        if(feriados) {
            const feriadosFromArray = feriados.split(",")

            this.feriados = feriadosFromArray
        }
    }

    calcularPeriodoDisponivel() {
        const agendamento = document.querySelector('#agendamento');
        const cutoffHour = Number(agendamento?.dataset?.cutoffHour ?? 17);

        let brtNow = new Date(this.today.toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
        let minDate = brtNow.getHours() >= cutoffHour ? this.getNextBusinessDay(new Date(brtNow)) : brtNow;

        while (this.diasUteisAdicionados < this.daysAvailable) {
            this.maxDate.setDate(this.maxDate.getDate() + 1);
            if (this.isDiaUtil(this.maxDate)) {
                this.diasUteisAdicionados++;
            }
        }

        this.minDate = minDate;
    }

    configurarFlatpickr() {
        flatpickr("#data", {
            locale: Portuguese,
            minDate: this.minDate,
            maxDate: this.maxDate,
            dateFormat: "d/m/Y",
            disable: [(date) => !this.isDiaUtil(date)],
            onChange: (selectedDates, dateStr, instance) => this.handleDateChange(selectedDates, dateStr, instance)
        });
    }

    isDiaUtil(date) {
        return date.getDay() !== 0 && !this.feriados.includes(this.formatDate(date));
    }

    normalizeDate(date) {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    isDateSelectable(date) {
        const normalized = this.normalizeDate(date);
        const min = this.normalizeDate(this.minDate);
        const max = this.normalizeDate(this.maxDate);
        return normalized >= min && normalized <= max && this.isDiaUtil(normalized);
    }

    setCheckoutButtonsDisabled(disabled) {
        document.querySelectorAll("[data-submit]").forEach(button => {
            if (disabled) {
                button.setAttribute("disabled", "true");
                button.setAttribute("data-disabled", "true");
            } else {
                button.removeAttribute("disabled");
                button.removeAttribute("data-disabled");
            }
        });
    }

    handleDateChange(selectedDates, dateStr, instance) {
        const selectedDate = selectedDates[0];
        if (!selectedDate || !this.isDateSelectable(selectedDate)) {
            this.setCheckoutButtonsDisabled(true);
            instance?.clear();
            console.warn('Data selecionada fora do intervalo permitido ou em dia indisponível.');
            return;
        }

        document.querySelectorAll('[name="attributes[Data agendamento:]"]').forEach(input => input.value = dateStr);
        document.querySelectorAll('[name="note"]').forEach(input => input.value = dateStr);

        this.setCheckoutButtonsDisabled(true);

        console.log('Atualizando carrinho com a data selecionada:', dateStr);

        fetch('/cart.js')
            .then(response => response.json())
            .then(cart => {
                const attributes = { ...cart.attributes, 'Data agendamento:': dateStr };

                return fetch('/cart/update.js', {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Accept": "application/json" },
                    body: JSON.stringify({ note: dateStr, attributes })
                });
            })
            .then(response => {
                if (!response.ok) throw new Error('Falha ao atualizar o carrinho com a data selecionada.');
                this.setCheckoutButtonsDisabled(false);
            })
            .catch(error => {
                console.error('Erro ao atualizar carrinho:', error);
                this.setCheckoutButtonsDisabled(false);
            });
    }

    calcularFeriadosVariaveis(ano) {
        let a = ano % 19;
        let b = Math.floor(ano / 100);
        let c = ano % 100;
        let d = Math.floor(b / 4);
        let e = b % 4;
        let f = Math.floor((b + 8) / 25);
        let g = Math.floor((b - f + 1) / 3);
        let h = (19 * a + b - d - g + 15) % 30;
        let i = Math.floor(c / 4);
        let k = c % 4;
        let l = (32 + 2 * e + 2 * i - h - k) % 7;
        let m = Math.floor((a + 11 * h + 22 * l) / 451);
        let mes = Math.floor((h + l - 7 * m + 114) / 31);
        let dia = ((h + l - 7 * m + 114) % 31) + 1;

        let pascoa = new Date(ano, mes - 1, dia);
        return [
            this.formatDate(new Date(pascoa.setDate(pascoa.getDate() - 47))), // Carnaval
            this.formatDate(new Date(pascoa.setDate(pascoa.getDate() + 45))), // Sexta-feira Santa
            this.formatDate(new Date(pascoa.setDate(pascoa.getDate() + 60)))  // Corpus Christi
        ];
    }

    formatDate(date) {
        return date.toISOString().split('T')[0];
    }

    getNextBusinessDay(date) {
        do {
            date.setDate(date.getDate() + 1);
        } while (!this.isDiaUtil(date));
        return date;
    }
}

customElements.define('data-select', DataSelect);

class ButtonRemoveKit extends HTMLElement {
    constructor() {
        super()

        this.hasMinicart = this.hasAttribute("data-minicart")
        this.init()
    }

    init() {
        this.addEventListener("click", (event) => this.handleRemove(event))
    }

    async  handleRemove(event) {
        event.preventDefault();
        const { currentTarget } = event

        try {
            const montagemGroup = currentTarget.getAttribute('data-montagem-group'); // Pega o grupo de montagem do pai
            if (!this.hasMinicart) currentTarget.innerHTML = '<span>Removendo...</span>';

            if (this.hasMinicart) {
                const spinner = this.querySelector('.loading__spinner ');
                spinner.classList.remove('hidden');

                const spanText = this.querySelector('span:not(.loading__spinner )');
                if (spanText) spanText.classList.add('hidden');
            }

            currentTarget.style.pointerEvents = 'none'; // Desabilita o botão durante o processo

            const response = await fetch('/cart.js');
            const body = await response.json();

            if(body.items) {
                await this.removeItemMotagem(body.items, montagemGroup)
            }
        } catch (error) {
            console.log(`Erro handleRemove: ${error}`)
        } finally {
            if (!this.hasMinicart) currentTarget.innerHTML = '<span>Remover</span>';

            currentTarget.style.pointerEvents = 'all';
        }
    }

    async removeItemMotagem(items, montagemGroup) {
        try {
            const updates = Object.fromEntries(
                items
                    .filter((item) => item.properties && item.properties.Montagem === montagemGroup)
                    .map(item => [item.key, 0])
            );

            fetch(window.Shopify.routes.root + 'cart/update.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ updates })
            })
                .then((response) => response.json())
                .then(async (response) => {
                    if (this.hasMinicart && window.location.pathname !== '/cart') {
                        const response = await fetch('/cart?section_id=cart-drawer');
                        const responseText = await response.text();

                        const html = new DOMParser().parseFromString(responseText, "text/html");
                        const selectors = ['cart-items', '.drawer__heading', '.totals__subtotal-value', '.free-shipping-goal' ];

                        for (const selector of selectors) {                            
                            const targetElement = document.querySelector(selector);
                            const sourceElement = html.querySelector(selector);

                            if (targetElement && sourceElement) {
                                targetElement.replaceWith(sourceElement);
                            }
                        }

                        return
                    }

                    window.location.reload();
                })
                .catch((error) => {
                    console.error('Error:', error);
                });
        } catch (error) {
            console.log(`Erro removeItemMotagem: ${error}`)
        }
    }

}

customElements.define("button-remove-kit", ButtonRemoveKit)